import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, CalendarRange, Download, Info,
  ArrowUp, ArrowDown, Tag, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';

const API_BASE = 'https://api.glowytics.com';
const PAGE_SIZE = 50;

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => `${Math.round(Number(n || 0)).toLocaleString('tr-TR')} ₺`;
const fmtPct = (n) => (n == null ? '—' : `%${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};
const dayLabel = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  } catch { return d; }
};

const RANGES = [
  { key: '14', label: 'Son 14 gün' },
  { key: '30', label: 'Son 30 gün' },
  { key: '60', label: 'Son 60 gün' },
  { key: 'all', label: 'Tüm dönem' },
];
const SORTS = [
  { key: 'delta_desc', label: 'En pahalı olduğumuz' },
  { key: 'delta_asc', label: 'En ucuz olduğumuz' },
  { key: 'dayspricier_desc', label: 'En çok gün pahalı kalan' },
  { key: 'gap_desc', label: 'En büyük ₺ farkı' },
];
const FILTERS = [
  { key: 'ALL', label: 'Tümü' },
  { key: 'PRICIER', label: 'Sadece pahalı olduklarımız' },
  { key: 'CHEAPER', label: 'Sadece ucuz olduklarımız' },
  { key: 'OVER25', label: 'Kritik (>%25 pahalı)' },
];

export default function High5PriceCompetitivenessDaily() {
  const { selectedBrand } = useAuth();
  const [rangeKey, setRangeKey] = useState('30');
  const [sort, setSort] = useState('delta_desc');
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/sku-feed/price-competitiveness-daily?days=${rangeKey}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.detail || `HTTP ${res.status}`);
      }
      setPayload(await res.json());
      setPage(0);
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Veri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, rangeKey]);

  const days = useMemo(() => payload?.days || [], [payload]);
  const allProducts = useMemo(() => payload?.products || [], [payload]);
  const range = payload?.range || {};
  const last = days[days.length - 1] || null;
  const first = days[0] || null;

  const chartData = useMemo(() => days.map((d) => ({
    label: dayLabel(d.date),
    date: d.date,
    Pahalı: d.pricier,
    Ucuz: d.cheaper,
    Eşit: d.equal,
    delta: d.avgDelta,
  })), [days]);

  const products = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = allProducts.filter((p) => {
      if (filter === 'PRICIER' && !(p.deltaPct > 0)) return false;
      if (filter === 'CHEAPER' && !(p.deltaPct < 0)) return false;
      if (filter === 'OVER25' && !(p.deltaPct > 25)) return false;
      if (q && !`${p.title || ''} ${p.brand || ''} ${p.offerId || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const gap = (p) => (Number(p.ourPrice || 0) - Number(p.benchmarkPrice || 0));
    const cmp = {
      delta_desc: (a, b) => (b.deltaPct ?? -1e9) - (a.deltaPct ?? -1e9),
      delta_asc: (a, b) => (a.deltaPct ?? 1e9) - (b.deltaPct ?? 1e9),
      dayspricier_desc: (a, b) => b.daysPricier - a.daysPricier,
      gap_desc: (a, b) => gap(b) - gap(a),
    }[sort];
    return [...out].sort(cmp);
  }, [allProducts, filter, search, sort]);

  const paged = products.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(products.length / PAGE_SIZE));

  const exportCsv = () => {
    const head = ['Urun', 'Marka', 'Kod', 'Bizim fiyat', 'Piyasa benchmark', 'Fark %', 'Fark TL',
      'Kac gun pahali', 'Kac gun goruldu', 'Ortalama fark %'];
    const lines = products.map((p) => [
      p.title || '', p.brand || '', p.offerId || '',
      Math.round(p.ourPrice || 0), Math.round(p.benchmarkPrice || 0),
      p.deltaPct ?? '', Math.round((p.ourPrice || 0) - (p.benchmarkPrice || 0)),
      p.daysPricier, p.daysSeen, p.avgDelta ?? '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const daysHead = ['Tarih', 'Urun', 'Pahali', 'Ucuz', 'Esit', 'Pahali %', 'Ortalama fark %', '>25% pahali'];
    const dayLines = days.map((d) => [d.date, d.products, d.pricier, d.cheaper, d.equal,
      d.pricierPct, d.avgDelta, d.bands.over25].map((v) => `"${v}"`).join(','));
    const csv = [
      'GUNLUK OZET', daysHead.join(','), ...dayLines, '', 'URUN BAZINDA', head.join(','), ...lines,
    ].join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `fiyat-rekabeti-gunluk_${range.start || ''}_${range.end || ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deltaBadge = (v) => {
    if (v == null) return <span className="text-gray-300">—</span>;
    const tone = v > 25 ? 'bg-red-100 text-red-700'
      : v > 0 ? 'bg-amber-100 text-amber-700'
      : v < 0 ? 'bg-emerald-100 text-emerald-700'
      : 'bg-gray-100 text-gray-500';
    return (
      <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-bold tabular-nums ${tone}`}>
        {v > 0 ? <ArrowUp className="w-3 h-3" /> : v < 0 ? <ArrowDown className="w-3 h-3" /> : null}
        {fmtPct(Math.abs(v))}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
                <Tag className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Günlük Fiyat Rekabeti</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Hangi gün kaç üründe piyasadan <strong>pahalı</strong>, kaçında <strong>ucuz</strong> kaldık.
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={rangeKey} onChange={(e) => setRangeKey(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
                {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <button type="button" onClick={exportCsv} disabled={loading || products.length === 0}
                className="flex items-center gap-2 px-3 h-10 bg-white border rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button type="button" onClick={fetchData} disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Son gün özeti */}
          {last && (
            <div className="mt-4 rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50 to-white p-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-3">
                  <CalendarRange className="w-5 h-5 text-amber-600" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-amber-700/70 font-semibold">Son gün</div>
                    <div className="text-lg font-bold text-gray-900">{fmtDate(last.date)}</div>
                  </div>
                </div>
                <div className="h-8 w-px bg-amber-100 hidden sm:block" />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-amber-700/70 font-semibold">Pahalı olduğumuz</div>
                  <div className="text-lg font-bold text-red-700 tabular-nums">
                    {fmtNumber(last.pricier)} <span className="text-sm font-normal text-gray-400">/ {fmtNumber(last.products)} ürün ({fmtPct(last.pricierPct)})</span>
                  </div>
                </div>
                <div className="h-8 w-px bg-amber-100 hidden sm:block" />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-amber-700/70 font-semibold">Ucuz olduğumuz</div>
                  <div className="text-lg font-bold text-emerald-700 tabular-nums">{fmtNumber(last.cheaper)}</div>
                </div>
                <div className="h-8 w-px bg-amber-100 hidden sm:block" />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-amber-700/70 font-semibold">Kritik (&gt;%25 pahalı)</div>
                  <div className="text-lg font-bold text-red-700 tabular-nums">{fmtNumber(last.bands.over25)}</div>
                </div>
                {first && (
                  <>
                    <div className="h-8 w-px bg-amber-100 hidden sm:block" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-amber-700/70 font-semibold">İlk güne göre</div>
                      <div className="text-lg font-bold tabular-nums">
                        <span className={last.pricier > first.pricier ? 'text-red-700' : last.pricier < first.pricier ? 'text-emerald-700' : 'text-gray-500'}>
                          {last.pricier - first.pricier > 0 ? '+' : ''}{fmtNumber(last.pricier - first.pricier)}
                        </span>
                        <span className="text-sm font-normal text-gray-400"> pahalı ürün</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 flex gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <Info className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
            <div className="text-xs text-gray-600 leading-relaxed">
              Kaynak <strong>Merchant Center fiyat rekabet benchmark'ı</strong> — Google'ın aynı ürünü satan
              tüm satıcılardan hesapladığı piyasa fiyatı. Günlük snapshot, 06:00 cron'la yazılıyor.
              <strong> Rakip Fiyat Karşılaştırması sayfasıyla aynı şey değil:</strong> orası adı geçen
              5-6 siteden scrape edilir ve yalnızca eşleşen ürünleri kapsar; burası tüm piyasayı.
              Sadece benchmark'ı olan ürünler sayılır (ürün sayısı günlük dalgalanır).
            </div>
          </div>
        </div>

        {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {loading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}

        {/* Gün gün grafik */}
        {!loading && chartData.length > 0 && (
          <div className="p-6 border-b">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">GÜN GÜN: KAÇ ÜRÜNDE PAHALIYIZ</h3>
            <p className="text-xs text-gray-500 mb-3">
              Çubuklar ürün adedi (yığılmış), çizgi o günün ortalama fiyat farkı (%). Çizgi yukarı gidiyorsa
              piyasadan uzaklaşıyoruz.
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid vertical={false} stroke="#f1f2f4" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={40} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `%${v}`}
                       tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={44} />
                <RTooltip
                  formatter={(v, n) => (n === 'delta' ? [fmtPct(v), 'Ortalama fark'] : [fmtNumber(v), n])}
                  labelFormatter={(l, p) => fmtDate(p?.[0]?.payload?.date)}
                />
                <Legend verticalAlign="top" height={28} iconType="square" wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="Pahalı" stackId="a" fill="#dc2626" maxBarSize={26} />
                <Bar yAxisId="left" dataKey="Eşit" stackId="a" fill="#d1d5db" maxBarSize={26} />
                <Bar yAxisId="left" dataKey="Ucuz" stackId="a" fill="#059669" maxBarSize={26} />
                <Line yAxisId="right" type="monotone" dataKey="delta" name="delta" stroke="#b45309"
                      strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Gün tablosu */}
        {!loading && days.length > 0 && (
          <div className="border-b">
            <div className="px-6 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-gray-800">GÜNLÜK ÖZET</h3>
            </div>
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-y sticky top-0">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Tarih</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Ürün</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-red-600 uppercase">Pahalı</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Pahalı %</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-emerald-600 uppercase">Ucuz</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Eşit</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Ort. fark</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">&gt;%25</th>
                  </tr>
                </thead>
                <tbody>
                  {[...days].reverse().map((d) => (
                    <tr key={d.date} className="border-b last:border-0 hover:bg-amber-50/30">
                      <td className="px-4 py-2 text-gray-800">{fmtDate(d.date)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmtNumber(d.products)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-red-700">{fmtNumber(d.pricier)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmtPct(d.pricierPct)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-emerald-700">{fmtNumber(d.cheaper)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-400">{fmtNumber(d.equal)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">{fmtPct(d.avgDelta)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-red-600">{fmtNumber(d.bands.over25)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ürün filtreleri */}
        {!loading && allProducts.length > 0 && (
          <>
            <div className="p-4 bg-gray-50/60 border-b flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  placeholder="Ürün, marka veya kod ara…" className="w-full pl-9 pr-3 py-2 bg-white border rounded-xl text-sm" />
              </div>
              <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                className="px-3 py-2 bg-white border rounded-xl text-sm">
                {FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }}
                className="px-3 py-2 bg-white border rounded-xl text-sm">
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>

            <div className="px-6 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-gray-800">
                ÜRÜN BAZINDA <span className="font-normal text-gray-400">· {fmtNumber(products.length)} ürün</span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-y">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Ürün</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Bizim</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Piyasa</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Fark ₺</th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Fark %</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase"
                        title="Seçili dönemde kaç gün piyasadan pahalıydı">Kaç gün pahalı</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => {
                    const gap = Number(p.ourPrice || 0) - Number(p.benchmarkPrice || 0);
                    return (
                      <tr key={p.productId} className="border-b last:border-0 hover:bg-amber-50/30">
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {p.image ? (
                              <img src={p.image} alt="" loading="lazy" className="w-8 h-8 rounded object-cover border border-gray-100 bg-white shrink-0" />
                            ) : <div className="w-8 h-8 rounded bg-gray-100 shrink-0" />}
                            <div className="min-w-0">
                              <div className="font-medium text-gray-800 truncate max-w-[280px] text-xs" title={p.title}>{p.title}</div>
                              <div className="text-[10px] text-gray-400 truncate max-w-[280px]">{p.brand}{p.brand && p.offerId ? ' · ' : ''}{p.offerId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-800">{p.ourPrice != null ? fmtMoney(p.ourPrice) : '—'}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-500">{p.benchmarkPrice != null ? fmtMoney(p.benchmarkPrice) : '—'}</td>
                        <td className={`px-4 py-2 text-right tabular-nums font-medium ${gap > 0 ? 'text-red-600' : gap < 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {p.ourPrice != null ? `${gap > 0 ? '+' : ''}${fmtMoney(gap)}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-center">{deltaBadge(p.deltaPct)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                          {fmtNumber(p.daysPricier)}<span className="text-gray-300">/{fmtNumber(p.daysSeen)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {products.length > PAGE_SIZE && (
              <div className="p-4 border-t bg-white flex items-center justify-between text-sm text-gray-600">
                <div>{fmtNumber(products.length)} üründen {page * PAGE_SIZE + 1}–{Math.min(products.length, (page + 1) * PAGE_SIZE)} arası</div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                    className="flex items-center justify-center w-9 h-9 bg-white border rounded-lg disabled:opacity-40">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="tabular-nums">{page + 1} / {pageCount}</span>
                  <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                    className="flex items-center justify-center w-9 h-9 bg-white border rounded-lg disabled:opacity-40">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
